import { makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { Skeleton, SkeletonItem } from '@fluentui/react-skeleton';
import React from 'react';
import { Breakpoints } from '../../styles';

const useClasses = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXL,
        padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalL}`,
        width: '100%',
        ...Breakpoints.small({
            padding: tokens.spacingVerticalL,
        }),
    },
    messageWrapper: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: tokens.spacingHorizontalM,
        width: '100%',
        ...Breakpoints.small({
            gap: tokens.spacingHorizontalS,
        }),
    },
    messageWrapperUser: {
        flexDirection: 'row-reverse',
    },
    avatarContainer: {
        flexShrink: 0,
        paddingTop: tokens.spacingVerticalS,
    },
    contentContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
        flex: 1,
        maxWidth: '75%',
        ...Breakpoints.small({
            maxWidth: '100%',
        }),
    },
    messageBubble: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
        ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: '2px 20px 20px 20px',
        minHeight: '60px',
        width: '100%',
    },
    messageBubbleUser: {
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: '20px 20px 2px 20px',
    },
    messageText: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    messageLineShort: {
        width: '70%',
    },
    messageLineLong: {
        width: '90%',
    },
    timestamp: {
        display: 'flex',
        alignItems: 'center',
        marginTop: tokens.spacingVerticalXS,
    },
    timestampSkeleton: {
        width: '60px',
    },
});

interface ChatSkeletonProps {
    /**
     * Number of skeleton message items to display
     * @default 3
     */
    count?: number;

    /**
     * Optional custom CSS class for styling
     */
    className?: string;
}

export const ChatSkeleton: React.FC<ChatSkeletonProps> = ({ count = 3, className }) => {
    const classes = useClasses();

    // Generate skeleton items with alternating alignment
    const items = Array.from({ length: count }, (_, index) => ({
        id: `skeleton-${index}`,
        isUser: index % 2 === 1, // Odd indices are user messages
    }));

    return (
        <div
            className={className ?? classes.container}
            role="status"
            aria-live="polite"
            aria-label="Mesajlar yükleniyor"
            aria-busy="true"
        >
            {items.map((item) => (
                <Skeleton key={item.id} animation="wave">
                    <div
                        className={item.isUser ? `${classes.messageWrapper} ${classes.messageWrapperUser}` : classes.messageWrapper}
                    >
                        {/* Avatar */}
                        <div className={classes.avatarContainer}>
                            <SkeletonItem shape="circle" size={32} />
                        </div>

                        {/* Message Content */}
                        <div className={classes.contentContainer}>
                            <div
                                className={item.isUser ? `${classes.messageBubble} ${classes.messageBubbleUser}` : classes.messageBubble}
                            >
                                {/* Message Lines */}
                                <div className={classes.messageText}>
                                    <SkeletonItem className={classes.messageLineLong} size={16} />
                                    <SkeletonItem className={classes.messageLineShort} size={16} />
                                    <SkeletonItem size={16} style={{ width: '65%' }} />
                                </div>
                            </div>

                            {/* Timestamp */}
                            <div className={classes.timestamp}>
                                <SkeletonItem className={classes.timestampSkeleton} size={12} />
                            </div>
                        </div>
                    </div>
                </Skeleton>
            ))}
        </div>
    );
};
